/**
 * Emails domain on TotalAccessOfflineDB — encrypted payloads via offlinePayloadCrypto.
 */
import offlinePayloadCrypto from './offlineCrypto';
import type { OfflineMetaRecord, OfflineRecordSyncStatus } from './offlineDb';
import offlineDb, { type OfflineDomainRecord } from './offlineDb';
import { clearLegacyEncryptedStorageCategory } from './offlineDbMaintenance';

const DOMAIN = 'emails' as const;

export type EmailsStoreName = 'outbox_emails' | 'email_drafts';

export type EmailsMetaKey = 'email_sync' | 'legacy_migration_v1';

interface UpsertRecordOptions {
  status?: string;
  syncStatus?: OfflineRecordSyncStatus;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

function getDb() {
  return offlineDb;
}

function toIso(value?: Date | string | null, fallback?: string): string {
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === 'string' && value.trim()) {
    return value;
  }
  return fallback || new Date().toISOString();
}

function toEntityId(value: string | number | undefined | null): string {
  return String(value ?? '');
}

function emailStatusToSync(status: string): OfflineRecordSyncStatus {
  if (status === 'sent') {
    return 'synced';
  }
  if (status === 'failed') {
    return 'failed';
  }
  return 'pending';
}

async function decryptRecordPayload<T>(record: OfflineDomainRecord | undefined): Promise<T | undefined> {
  if (!record) {
    return undefined;
  }
  return offlinePayloadCrypto.decryptValue<T>(record.payload);
}

async function buildRecordRow<T extends Record<string, unknown>>(
  store: EmailsStoreName,
  clientId: number,
  entityId: string,
  item: T,
  existing: OfflineDomainRecord | undefined,
  options?: UpsertRecordOptions & { parentId?: string },
): Promise<OfflineDomainRecord> {
  const now = toIso(options?.updatedAt);
  const payload = await offlinePayloadCrypto.encryptValue(item);
  const explicitStatus = options?.status ?? (item.status as string | undefined);
  const rowStatus = typeof explicitStatus === 'string' ? explicitStatus : undefined;

  return {
    id: existing?.id,
    domain: DOMAIN,
    store,
    client_id: clientId,
    entity_id: entityId,
    parent_id: options?.parentId,
    status: rowStatus,
    sync_status:
      options?.syncStatus ??
      (existing?.sync_status || (rowStatus ? emailStatusToSync(rowStatus) : 'pending')),
    created_at: existing?.created_at || toIso(options?.createdAt, now),
    updated_at: now,
    payload,
    payload_size: payload.length,
  };
}

function buildMetaStorageKey(clientId: number, key: EmailsMetaKey): string {
  return `${DOMAIN}:${clientId}:${key}`;
}

async function getStoreRow(store: EmailsStoreName, clientId: number, entityId: string): Promise<OfflineDomainRecord | undefined> {
  const db = getDb();
  if (!db) {
    return undefined;
  }
  return db.records
    .where('[domain+store+client_id+entity_id]')
    .equals([DOMAIN, store, clientId, entityId])
    .first();
}

async function getStoreRows(store: EmailsStoreName, clientId: number): Promise<OfflineDomainRecord[]> {
  const db = getDb();
  if (!db) {
    return [];
  }
  return db.records.where('[domain+store+client_id]').equals([DOMAIN, store, clientId]).toArray();
}

const emailsStore = {
  async upsertOutboxEmail<T extends Record<string, unknown>>(
    clientId: number,
    offlineId: string,
    item: T,
    parentId: string | undefined,
    options?: UpsertRecordOptions,
  ): Promise<void> {
    const db = getDb();
    if (!db || !clientId) {
      return;
    }
    const existing = await getStoreRow('outbox_emails', clientId, offlineId);
    const row = await buildRecordRow('outbox_emails', clientId, offlineId, item, existing, {
      parentId: parentId ?? undefined,
      ...options,
    });
    await db.records.put(row);
  },

  async getOutboxEmail<T>(clientId: number, offlineId: string): Promise<T | undefined> {
    const row = await getStoreRow('outbox_emails', clientId, offlineId);
    return decryptRecordPayload<T>(row);
  },

  async deleteOutboxEmail(clientId: number, offlineId: string): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }
    const row = await getStoreRow('outbox_emails', clientId, offlineId);
    if (row?.id) {
      await db.records.delete(row.id);
    }
  },

  async listOutboxEmails<T>(clientId: number): Promise<T[]> {
    const rows = await getStoreRows('outbox_emails', clientId);
    const values = await Promise.all(rows.map((row) => decryptRecordPayload<T>(row)));
    return values.reduce<T[]>((acc, v) => {
      if (v !== undefined) {
        acc.push(v);
      }
      return acc;
    }, []);
  },

  async upsertDraft<T extends Record<string, unknown>>(
    clientId: number,
    draftIdKey: string,
    item: T,
    options?: UpsertRecordOptions,
  ): Promise<void> {
    const db = getDb();
    if (!db || !clientId) {
      return;
    }
    const existing = await getStoreRow('email_drafts', clientId, draftIdKey);
    const row = await buildRecordRow('email_drafts', clientId, draftIdKey, item, existing, options);
    await db.records.put(row);
  },

  async getDraft<T>(clientId: number, draftIdKey: string): Promise<T | undefined> {
    const row = await getStoreRow('email_drafts', clientId, draftIdKey);
    return decryptRecordPayload<T>(row);
  },

  async deleteDraft(clientId: number, draftIdKey: string): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }
    const row = await getStoreRow('email_drafts', clientId, draftIdKey);
    if (row?.id) {
      await db.records.delete(row.id);
    }
  },

  async listDraftsForUser<T extends { user_id?: number; updated_at?: Date | string }>(
    clientId: number,
    userId: number,
  ): Promise<T[]> {
    const rows = await getStoreRows('email_drafts', clientId);
    const values = await Promise.all(rows.map((row) => decryptRecordPayload<T>(row)));
    const out: T[] = [];
    for (const v of values) {
      if (v !== undefined && Number(v.user_id) === Number(userId)) {
        out.push(v);
      }
    }
    return out.sort((a, b) => {
      const ua = new Date(String(a.updated_at || 0)).getTime();
      const ub = new Date(String(b.updated_at || 0)).getTime();
      return ub - ua;
    });
  },

  async bulkDeleteSentOutboxBefore(clientId: number, cutoffIso: string): Promise<number> {
    const emails = await this.listOutboxEmails<Record<string, unknown>>(clientId);
    let deleted = 0;
    const cutoff = new Date(cutoffIso);
    for (const e of emails) {
      const status = String(e.status || '');
      if (status !== 'sent' || !e.sent_at) {
        continue;
      }
      const sent = e.sent_at instanceof Date ? e.sent_at : new Date(String(e.sent_at));
      if (sent < cutoff) {
        const oid = String(e.offline_id || '');
        if (oid) {
          await this.deleteOutboxEmail(clientId, oid);
          deleted++;
        }
      }
    }
    return deleted;
  },

  async setMeta<T>(clientId: number, key: EmailsMetaKey, value: T): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }
    const row: OfflineMetaRecord = {
      key: buildMetaStorageKey(clientId, key),
      domain: DOMAIN,
      client_id: clientId,
      updated_at: new Date().toISOString(),
      payload: await offlinePayloadCrypto.encryptValue(value),
    };
    await db.meta.put(row);
  },

  async getMeta<T>(clientId: number, key: EmailsMetaKey): Promise<T | null> {
    const db = getDb();
    if (!db) {
      return null;
    }
    const row = await db.meta.get(buildMetaStorageKey(clientId, key));
    if (!row) {
      return null;
    }
    return offlinePayloadCrypto.decryptValue<T>(row.payload);
  },

  async deleteMeta(clientId: number, key: EmailsMetaKey): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }
    await db.meta.delete(buildMetaStorageKey(clientId, key));
  },

  async clearClient(clientId: number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }
    const [recordRows, metaRows] = await Promise.all([
      db.records.where('[domain+client_id]').equals([DOMAIN, clientId]).toArray(),
      db.meta.where('[domain+client_id]').equals([DOMAIN, clientId]).toArray(),
    ]);
    const recordIds = recordRows.map((r) => r.id).filter((id): id is number => typeof id === 'number');
    const metaKeys = metaRows.map((r) => r.key);
    await db.transaction('rw', db.records, db.meta, async () => {
      if (recordIds.length > 0) {
        await db.records.bulkDelete(recordIds);
      }
      if (metaKeys.length > 0) {
        await db.meta.bulkDelete(metaKeys);
      }
    });
  },
};

export async function migrateLegacyEmailDatabaseIfNeeded(): Promise<void> {
  if (typeof window === 'undefined' || !offlineDb) {
    return;
  }
  const marker = await emailsStore.getMeta(0, 'legacy_migration_v1');
  if (marker) {
    return;
  }

  try {
    const DexieMod = await import('dexie');
    const Dexie = DexieMod.default;

    let legacyEmails: Record<string, unknown>[] = [];
    let legacyDrafts: Record<string, unknown>[] = [];
    try {
      const legacyDb = new Dexie('TotalAccessEmailOfflineDB');
      legacyDb.version(1).stores({
        emails: '++id, offline_id, client_id, user_id, mailbox_email, status, created_at, queued_at, [client_id+status]',
        drafts: '++id, draft_id, client_id, user_id, mailbox_email, updated_at, [client_id+user_id]',
        syncMetadata: 'id',
      });
      await legacyDb.open();
      legacyEmails = (await legacyDb.table('emails').toArray()) as Record<string, unknown>[];
      legacyDrafts = (await legacyDb.table('drafts').toArray()) as Record<string, unknown>[];
      await legacyDb.close();
      await Dexie.delete('TotalAccessEmailOfflineDB');
    } catch {
      // Legacy DB absent
    }

    for (const d of legacyDrafts) {
      const cid = Number(d.client_id || 0);
      const draftId = String(d.draft_id || '');
      if (!cid || !draftId) {
        continue;
      }
      await emailsStore.upsertDraft(cid, draftId, d as Record<string, unknown>);
    }

    const clientIds = new Set<number>();
    for (const row of legacyEmails) {
      const cid = Number(row.client_id || 0);
      const oid = String(row.offline_id || '');
      if (!cid || !oid) {
        continue;
      }
      clientIds.add(cid);
      const threadId = row.thread_id != null ? String(row.thread_id) : undefined;
      await emailsStore.upsertOutboxEmail(cid, oid, row as Record<string, unknown>, threadId, {});
    }

    for (const cid of clientIds) {
      await clearLegacyEncryptedStorageCategory('emails', cid).catch(() => undefined);
    }

    await emailsStore.setMeta(0, 'legacy_migration_v1', {
      migrated_at: new Date().toISOString(),
      rows: legacyEmails.length + legacyDrafts.length,
    });
  } catch (e) {
    console.warn('[emailsStore] Legacy migration skipped:', e);
  }
}

export default emailsStore;
